import { BrowserRouter as Router, useLocation, useNavigate, HashRouter } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { useEffect, useState } from "react";
import { supabase, isDevelopment, isStaging, isUserAdmin } from "./integrations/supabase/client";
import { useToast } from "./components/ui/use-toast";
import "@/i18n/config";
import AppRoutes from "./routes/AppRoutes";

// Determine if we should use hash routing based on domain
const useHashRouting = window.location.hostname === 'sakuwari.jp';

// Handle direct navigation - crucial for refreshes on sakuwari.jp
if (useHashRouting && !window.location.hash && window.location.pathname !== '/') {
  // Convert the pathname to a hash route and redirect
  window.location.replace(`/#${window.location.pathname}${window.location.search}`);
}

// Helper function to extract groupId from hash or pathname
const extractGroupId = (path: string) => {
  const inviteMatch = path.match(/\/invite\/([^\/\?]+)/);
  const groupMatch = path.match(/\/group\/([^\/\?]+)/);
  return inviteMatch ? inviteMatch[1] : groupMatch ? groupMatch[1] : null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Debug info
  console.log('App rendered with:', {
    path: location.pathname,
    hash: location.hash,
    hostname: window.location.hostname,
    useHashRouting,
    rawUrl: window.location.href,
    extractedGroupId: extractGroupId(useHashRouting ? location.hash.replace('#', '') : location.pathname)
  });

  // Show environment banner for non-production builds
  useEffect(() => {
    if (isStaging() || isDevelopment()) {
      const envName = isStaging() ? 'STAGING' : 'DEVELOPMENT';
      toast({
        title: `${envName} Environment`,
        description: "This is not the production app. Changes won't affect real users.",
        duration: 10000,
        className: isStaging() ? "bg-amber-500 text-white" : "bg-blue-500 text-white"
      });
    }
  }, [toast]);

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // If accessing admin routes, check admin status
        if (location.pathname.includes('/admin')) {
          try {
            const isAdmin = await isUserAdmin(session.user.id);
            console.log('Admin check result:', { isAdmin, userId: session.user.id });
            
            if (!isAdmin) {
              toast({
                title: "Access Denied",
                description: "You don't have permission to access this page",
                variant: "destructive",
              });
              navigate('/app');
              return;
            }
          } catch (error) {
            console.error('Error checking admin status:', error);
            navigate('/app');
            return;
          }
        }
        
        setUser(session.user);
      }
      setLoading(false);

      // Only redirect to /app if not on an admin page and user is logged in
      if (session?.user && location.pathname === '/login' && !location.pathname.includes('/admin')) {
        navigate('/app');
      }
    });

    // Auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const newUser = session?.user ?? null;
      setUser(newUser);

      // If user just logged in
      if (newUser) {
        console.log("User logged in, checking for pendingInvite");
        
        // Check if there's a pending group invite
        const pendingInvite = localStorage.getItem('pendingInvite');
        
        if (pendingInvite) {
          console.log("Found pending invite:", pendingInvite);
          // Clear the pending invite immediately to prevent loops
          localStorage.removeItem('pendingInvite');
          
          try {
            // Check if user is already a member of this group
            const { data: existingMember, error: checkError } = await supabase
              .from('group_members')
              .select('id')
              .eq('group_id', pendingInvite)
              .eq('profile_id', newUser.id)
              .maybeSingle();

            if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
              throw checkError;
            }

            // If user is already a member, just navigate to the group
            if (existingMember) {
              console.log("User already a member, redirecting to group");
              toast({
                description: "You're already a member of this group!",
                className: "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
              });
              navigate(`/group/${pendingInvite}`);
              return;
            }

            console.log("Adding user to group after signup");
            // Add user to the group
            const { error } = await supabase
              .from('group_members')
              .insert({
                group_id: pendingInvite,
                profile_id: newUser.id,
                is_admin: false
              });

            if (error) throw error;

            toast({
              description: "You've successfully joined the group!",
              className: "bg-gradient-to-r from-green-500 to-emerald-500 text-white"
            });

            // Redirect to the group page
            navigate(`/group/${pendingInvite}`);
          } catch (error) {
            console.error('Error joining group after auth:', error);
            toast({
              title: "Error",
              description: "Failed to join the group. Please try again.",
              variant: "destructive",
            });
            navigate('/app');
          }
        } else if (location.pathname === '/login' && !location.pathname.includes('/admin')) {
          // Only redirect to /app if on login page and not accessing admin routes
          navigate('/app');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [location.pathname, navigate, toast]);

  if (loading) {
    return null;
  }

  return <AppRoutes user={user} />;
}

const AppWrapper = () => {
  // Debug info
  console.log('AppWrapper rendered at:', new Date().toISOString());
  console.log('Current hostname:', window.location.hostname);
  console.log('Using hash routing:', useHashRouting);
  console.log('Current path:', window.location.pathname);
  console.log('Current hash:', window.location.hash);

  // Extract groupId from URL to help debugging
  const path = useHashRouting ? window.location.hash.replace('#', '') : window.location.pathname;
  const groupId = extractGroupId(path);
  if (groupId) {
    console.log('Detected groupId in URL:', groupId);
  }

  // Show an environment indicator UI element for non-production environments
  const EnvironmentIndicator = () => {
    if (isDevelopment()) {
      return <div className="fixed bottom-0 left-0 z-50 bg-blue-600 text-white text-xs px-2 py-1 rounded-tr-md">DEV</div>;
    }
    if (isStaging()) {
      return <div className="fixed bottom-0 left-0 z-50 bg-amber-600 text-white text-xs px-2 py-1 rounded-tr-md">STAGING</div>;
    }
    return null;
  };

  // We need to ensure there's a proper interception of all routes
  if (useHashRouting) {
    return (
      <HashRouter>
        <EnvironmentIndicator />
        <App />
        <Toaster />
      </HashRouter>
    );
  } else {
    return (
      <Router>
        <EnvironmentIndicator />
        <App />
        <Toaster />
      </Router>
    );
  }
};

export default AppWrapper;
